import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search,
  Filter,
  MoreHorizontal,
  Eye,
  EyeOff,
  Trash2,
  Flag,
  MessageSquare,
  Image,
  Video,
  FileText,
  AlertTriangle,
  CheckCircle,
  Clock,
  ExternalLink,
  RefreshCw,
  Pin,
  PinOff,
  Lock,
  Unlock,
  RotateCcw,
  HardDrive,
  Users,
  Database,
  BarChart3,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useAdminDiscussions,
  useAdminDeletedDiscussions,
  useAdminRestoreDeletedDiscussion,
  useAdminMedia,
  useHideContent,
  useDeleteContent,
  useHideMedia,
  useRemoveMedia,
  useRestoreMedia,
  useStorageStats,
} from '@/hooks/use-admin';
import { useCategories } from '@/hooks/use-categories';
import { useForum } from '@/hooks/use-forum';
import { formatDistanceToNow } from 'date-fns';

// Discussion Content Tab
function DiscussionsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContent, setSelectedContent] = useState(null);
  const [actionDialog, setActionDialog] = useState({
    open: false,
    type: null,
    content: null,
  });

  const currentPage = parseInt(searchParams.get('page') || '1');
  const statusFilter = searchParams.get('status') || 'all';
  const typeFilter = searchParams.get('type') || 'all';

  const { data, isLoading, refetch } = useAdminDiscussions({
    page: currentPage,
    limit: 20,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    type: typeFilter !== 'all' ? typeFilter : undefined,
    search: searchQuery || undefined,
  });

  const hideContent = useHideContent();
  const deleteContent = useDeleteContent();

  // Pin/Freeze hooks
  const { pinDiscussion, unpinDiscussion } = useCategories();
  const { freezeDiscussion, unfreezeDiscussion } = useForum();

  const handleFilterChange = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handlePageChange = (newPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', newPage.toString());
    setSearchParams(newParams);
  };

  const handleAction = async (type, content) => {
    setActionDialog({ open: true, type, content });
  };

  const handlePin = async (content) => {
    try {
      if (content.pinned) {
        await unpinDiscussion.mutateAsync({
          categoryId: content.categoryId,
          name: content.category || content.slug, // Category name
          discussionOid: content.oid,
        });
      } else {
        await pinDiscussion.mutateAsync({
          categoryId: content.categoryId,
          name: content.category || content.slug, // Category name
          discussionOid: content.oid,
        });
      }
      refetch();
    } catch (error) {
      console.error('Pin action failed:', error);
    }
  };

  const handleFreeze = async (content) => {
    try {
      if (content.frozen) {
        await unfreezeDiscussion().mutateAsync({
          discussionOid: content.oid,
        });
      } else {
        await freezeDiscussion().mutateAsync({
          discussionOid: content.oid,
        });
      }
      refetch();
    } catch (error) {
      console.error('Freeze action failed:', error);
    }
  };

  const confirmAction = async () => {
    const { type, content } = actionDialog;
    try {
      if (type === 'hide') {
        await hideContent.mutateAsync({
          entityType: 'discussion',
          entityId: content._id,
          reason: 'Hidden by admin',
        });
      } else if (type === 'delete') {
        await deleteContent.mutateAsync({
          entityType: 'discussion',
          entityId: content._id,
          reason: 'Deleted by admin',
        });
      }
      refetch();
    } catch (error) {
      console.error('Action failed:', error);
    }
    setActionDialog({ open: false, type: null, content: null });
  };

  const getStatusBadge = (status, item) => {
    const badges = [];

    // Check for pinned state
    if (item?.pinned) {
      badges.push(
        <Badge
          key="pinned"
          variant="default"
          className="flex items-center gap-1 bg-blue-500"
        >
          <Pin className="h-3 w-3" />
          Pinned
        </Badge>
      );
    }

    // Check for frozen state
    if (item?.frozen) {
      badges.push(
        <Badge
          key="frozen"
          variant="secondary"
          className="flex items-center gap-1 bg-amber-500 text-white"
        >
          <Lock className="h-3 w-3" />
          Frozen
        </Badge>
      );
    }

    // Add regular status badge
    const variants = {
      published: { variant: 'default', icon: CheckCircle },
      active: { variant: 'default', icon: CheckCircle },
      hidden: { variant: 'secondary', icon: EyeOff },
      flagged: { variant: 'destructive', icon: Flag },
      pending: { variant: 'outline', icon: Clock },
    };
    const { variant, icon: Icon } = variants[status] || variants.pending;

    // Only show regular status if no special badges, or if it's not 'active'/'published'
    if (
      badges.length === 0 ||
      (status !== 'active' && status !== 'published')
    ) {
      badges.push(
        <Badge
          key="status"
          variant={variant}
          className="flex items-center gap-1"
        >
          <Icon className="h-3 w-3" />
          {status}
        </Badge>
      );
    }

    return <div className="flex flex-wrap gap-1">{badges}</div>;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'discussion':
        return <MessageSquare className="h-4 w-4" />;
      case 'comment':
        return <FileText className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search discussions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-2">
          <Select
            value={statusFilter}
            onValueChange={(v) => handleFilterChange('status', v)}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="hidden">Hidden</SelectItem>
              <SelectItem value="flagged">Flagged</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={typeFilter}
            onValueChange={(v) => handleFilterChange('type', v)}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="discussion">Discussions</SelectItem>
              <SelectItem value="comment">Comments</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Content</TableHead>
              <TableHead>Author</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Reports</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <div className="flex items-center justify-center gap-2">
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Loading content...
                  </div>
                </TableCell>
              </TableRow>
            ) : !data?.discussions || data?.discussions?.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-8 text-muted-foreground"
                >
                  No content found
                </TableCell>
              </TableRow>
            ) : (
              data?.discussions?.map((item) => (
                <TableRow key={item._id || item.oid}>
                  <TableCell className="max-w-[300px]">
                    <div className="truncate font-medium">
                      {item.title || item.text?.substring(0, 50) || 'Untitled'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">
                        {item.creatorUsername ||
                          item.creator?.substring(0, 10) ||
                          'Unknown'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {getTypeIcon('discussion')}
                      <span className="capitalize">Discussion</span>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(item.status, item)}</TableCell>
                  <TableCell>
                    {(item.pendingReports || item.reportCount || 0) > 0 ? (
                      <Badge
                        variant="destructive"
                        className="flex items-center gap-1 w-fit"
                      >
                        <Flag className="h-3 w-3" />
                        {item.pendingReports || item.reportCount}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {item.createdAt
                      ? formatDistanceToNow(new Date(item.createdAt), {
                          addSuffix: true,
                        })
                      : 'N/A'}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => setSelectedContent(item)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handlePin(item)}>
                          {item.pinned ? (
                            <>
                              <PinOff className="h-4 w-4 mr-2" />
                              Unpin
                            </>
                          ) : (
                            <>
                              <Pin className="h-4 w-4 mr-2" />
                              Pin
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleFreeze(item)}>
                          {item.frozen ? (
                            <>
                              <Unlock className="h-4 w-4 mr-2" />
                              Unfreeze
                            </>
                          ) : (
                            <>
                              <Lock className="h-4 w-4 mr-2" />
                              Freeze
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleAction('hide', item)}
                        >
                          <EyeOff className="h-4 w-4 mr-2" />
                          {item.status === 'hidden' ? 'Unhide' : 'Hide'}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleAction('delete', item)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {data?.pagination && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(currentPage - 1) * 20 + 1} to{' '}
            {Math.min(currentPage * 20, data.pagination.total)} of{' '}
            {data.pagination.total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= data.pagination.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Content Details Dialog */}
      <Dialog
        open={!!selectedContent}
        onOpenChange={() => setSelectedContent(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Discussion Details</DialogTitle>
            <DialogDescription>
              Discussion by{' '}
              {selectedContent?.creatorUsername ||
                selectedContent?.creator?.substring(0, 10) ||
                'Unknown'}
            </DialogDescription>
          </DialogHeader>
          {selectedContent && (
            <div className="space-y-4">
              {selectedContent.title && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">
                    Title
                  </h4>
                  <p className="mt-1">{selectedContent.title}</p>
                </div>
              )}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">
                  Content
                </h4>
                <div className="mt-1 whitespace-pre-wrap max-h-64 overflow-y-auto">
                  {selectedContent.text ||
                    selectedContent.htmlContent ||
                    'No content'}
                </div>
              </div>
              {selectedContent.thumbnail && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">
                    Thumbnail
                  </h4>
                  <img
                    src={selectedContent.thumbnail}
                    alt="Thumbnail"
                    loading="lazy"
                    className="mt-1 max-h-40 rounded-lg object-cover"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">
                    Status
                  </h4>
                  <div className="mt-1">
                    {getStatusBadge(selectedContent.status)}
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">
                    Reports
                  </h4>
                  <p className="mt-1">
                    {selectedContent.pendingReports ||
                      selectedContent.reportCount ||
                      0}{' '}
                    reports
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedContent(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <AlertDialog
        open={actionDialog.open}
        onOpenChange={(open) =>
          !open && setActionDialog({ open: false, type: null, content: null })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actionDialog.type === 'hide' ? 'Hide Content' : 'Delete Content'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionDialog.type === 'hide'
                ? 'This will hide the content from public view. You can unhide it later.'
                : 'This action cannot be undone. The content will be permanently deleted.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAction}
              className={
                actionDialog.type === 'delete'
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : ''
              }
            >
              {actionDialog.type === 'hide' ? 'Hide' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Media Content Tab
function MediaTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showStorageStats, setShowStorageStats] = useState(false);
  const [actionDialog, setActionDialog] = useState({
    open: false,
    type: null,
    media: null,
  });

  const currentPage = parseInt(searchParams.get('mediaPage') || '1');
  const categoryFilter = searchParams.get('mediaCategory') || 'all';
  const typeFilter = searchParams.get('mediaType') || 'all';

  const { data, isLoading, refetch } = useAdminMedia({
    page: currentPage,
    limit: 24,
    category: categoryFilter !== 'all' ? categoryFilter : undefined,
    type: typeFilter !== 'all' ? typeFilter : undefined,
    search: searchQuery || undefined,
  });

  const {
    data: storageData,
    isLoading: storageLoading,
    refetch: refetchStorage,
  } = useStorageStats();

  const hideMedia = useHideMedia();
  const deleteMedia = useRemoveMedia();
  const restoreMedia = useRestoreMedia();

  const handleFilterChange = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('mediaPage', '1');
    setSearchParams(newParams);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    refetch();
  };

  const handlePageChange = (newPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('mediaPage', newPage.toString());
    setSearchParams(newParams);
  };

  const handleAction = (type, media) => {
    setActionDialog({ open: true, type, media });
  };

  const confirmAction = async () => {
    const { type, media } = actionDialog;
    try {
      if (type === 'hide') {
        await hideMedia.mutateAsync({
          mediaId: media._id,
          reason: 'Hidden by admin',
        });
      } else if (type === 'delete') {
        await deleteMedia.mutateAsync({
          mediaId: media._id,
          reason: 'Deleted by admin',
        });
      } else if (type === 'restore') {
        await restoreMedia.mutateAsync({
          mediaId: media._id,
          url: media.url, // Pass the URL as fallback for restoration
        });
      }
      // Close both dialogs and refresh
      setSelectedMedia(null);
      refetch();
    } catch (error) {
      console.error('Action failed:', error);
    }
    setActionDialog({ open: false, type: null, media: null });
  };

  const getCategoryBadge = (category) => {
    const colors = {
      profile: 'bg-purple-500',
      discussion: 'bg-blue-500',
    };
    return (
      <Badge
        className={`text-xs text-white ${colors[category] || 'bg-gray-500'}`}
      >
        {category}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const labels = {
      avatar: 'Avatar',
      profile_banner: 'Profile Banner',
      banner: 'Banner',
      content: 'Inline Image',
    };
    return (
      <Badge variant="secondary" className="text-xs capitalize">
        {labels[type] || type}
      </Badge>
    );
  };

  return (
    <div className="space-y-4">
      {/* Storage Analytics Toggle */}
      <div className="flex items-center justify-between">
        <Button
          variant={showStorageStats ? 'default' : 'outline'}
          onClick={() => setShowStorageStats(!showStorageStats)}
          className="gap-2"
        >
          <HardDrive className="h-4 w-4" />
          Storage Analytics
          {storageData?.stats && (
            <Badge variant="secondary" className="ml-1">
              {storageData.stats.totalSizeFormatted}
            </Badge>
          )}
        </Button>
      </div>

      {/* Storage Stats Section */}
      {showStorageStats && (
        <div className="space-y-4">
          {/* Stats Overview Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <HardDrive className="h-4 w-4" />
                  Total Storage
                </CardDescription>
                <CardTitle className="text-2xl">
                  {storageLoading
                    ? '...'
                    : storageData?.stats?.totalSizeFormatted || '0 B'}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-xs text-muted-foreground">
                  {storageData?.stats?.totalObjects || 0} files
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Attributed
                </CardDescription>
                <CardTitle className="text-2xl">
                  {storageLoading
                    ? '...'
                    : storageData?.stats?.attributedSizeFormatted || '0 B'}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-xs text-muted-foreground">Linked to users</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  Unattributed
                </CardDescription>
                <CardTitle className="text-2xl">
                  {storageLoading
                    ? '...'
                    : storageData?.stats?.unattributedSize
                    ? `${(
                        storageData.stats.unattributedSize /
                        (1024 * 1024)
                      ).toFixed(2)} MB`
                    : '0 B'}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-xs text-muted-foreground">
                  Orphaned/system files
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Users
                </CardDescription>
                <CardTitle className="text-2xl">
                  {storageLoading
                    ? '...'
                    : storageData?.perUserStats?.length || 0}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-xs text-muted-foreground">
                  With media uploads
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Storage by Type */}
          {storageData?.stats?.byType &&
            storageData.stats.byType.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Storage by Type</CardTitle>
                  <CardDescription>Breakdown by media category</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {storageData.stats.byType.map((item) => (
                      <div key={item.type} className="flex items-center gap-4">
                        <div className="w-40 truncate text-sm font-medium">
                          {item.type.replace('/', ' / ')}
                        </div>
                        <div className="flex-1">
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary rounded-full transition-all"
                              style={{
                                width: `${Math.min(
                                  parseFloat(item.percentage),
                                  100
                                )}%`,
                              }}
                            />
                          </div>
                        </div>
                        <div className="w-20 text-right text-sm text-muted-foreground">
                          {item.formatted}
                        </div>
                        <div className="w-12 text-right text-xs text-muted-foreground">
                          {item.percentage}%
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

          {/* Per-User Storage Stats */}
          {storageData?.perUserStats && storageData.perUserStats.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Storage by User</CardTitle>
                <CardDescription>
                  All users with media uploads (
                  {storageData.perUserStats.length} users)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-h-[400px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Wallet</TableHead>
                        <TableHead className="text-right">
                          Profile Media
                        </TableHead>
                        <TableHead className="text-right">
                          Discussion Media
                        </TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Files</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {storageData.perUserStats.map((user, index) => (
                        <TableRow key={user.wallet}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground text-xs">
                                #{index + 1}
                              </span>
                              {user.username || 'Unknown'}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {user.wallet?.slice(0, 6)}...
                            {user.wallet?.slice(-4)}
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {user.profileMediaFormatted}
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {user.discussionMediaFormatted}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {user.totalSizeFormatted}
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {user.mediaCount}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchStorage()}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh Stats
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by username, wallet, or title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </form>
        <div className="flex gap-2">
          <Select
            value={categoryFilter}
            onValueChange={(v) => handleFilterChange('mediaCategory', v)}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="profile">Profile Media</SelectItem>
              <SelectItem value="discussion">Discussion Media</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={typeFilter}
            onValueChange={(v) => handleFilterChange('mediaType', v)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="avatar">Avatars</SelectItem>
              <SelectItem value="profile_banner">Profile Banners</SelectItem>
              <SelectItem value="banner">Discussion Banners</SelectItem>
              <SelectItem value="content">Inline Content</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Media Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !data?.media || data?.media?.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No media found
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {data?.media?.map((item) => (
            <Card
              key={item._id}
              className={`group relative overflow-hidden cursor-pointer hover:ring-2 transition-all ${
                item.isRestricted
                  ? 'ring-2 ring-red-500/50 hover:ring-red-500'
                  : 'hover:ring-primary'
              }`}
              onClick={() => setSelectedMedia(item)}
            >
              <div className="aspect-square bg-muted relative">
                <img
                  src={item.url}
                  alt={`${item.type} - ${item.uploaderUsername || 'Unknown'}`}
                  className={`w-full h-full object-cover ${
                    item.isRestricted ? 'opacity-50' : ''
                  }`}
                  loading="lazy"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"%3E%3Crect x="3" y="3" width="18" height="18" rx="2" ry="2"%3E%3C/rect%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"%3E%3C/circle%3E%3Cpolyline points="21,15 16,10 5,21"%3E%3C/polyline%3E%3C/svg%3E';
                    e.target.className =
                      'w-full h-full object-contain p-8 opacity-30';
                  }}
                />

                {/* Restricted Overlay */}
                {item.isRestricted && (
                  <div className="absolute inset-0 flex items-center justify-center bg-red-900/30">
                    <div className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded flex items-center gap-1">
                      <EyeOff className="h-3 w-3" />
                      HIDDEN
                    </div>
                  </div>
                )}

                {/* Overlay with info */}
                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                  <p className="text-white text-xs font-medium text-center">
                    {item.uploaderUsername ||
                      item.uploaderWallet?.slice(0, 10) ||
                      'Unknown'}
                  </p>
                  {item.discussionTitle && (
                    <p className="text-white/80 text-xs text-center line-clamp-2">
                      {item.discussionTitle}
                    </p>
                  )}
                  <div className="flex gap-1 flex-wrap justify-center">
                    {getCategoryBadge(item.category)}
                    {getTypeBadge(item.type)}
                    {item.isRestricted && (
                      <Badge className="bg-red-500 text-white text-xs">
                        Hidden
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Category badge - top left */}
                <div className="absolute top-2 left-2">
                  {getCategoryBadge(item.category)}
                </div>

                {/* Type badge - top right */}
                <div className="absolute top-2 right-2">
                  {getTypeBadge(item.type)}
                </div>
              </div>
              <CardContent className="p-2">
                <p className="text-xs text-muted-foreground truncate">
                  {item.uploaderUsername ||
                    item.uploaderWallet?.slice(0, 10) ||
                    'Unknown'}
                </p>
                {item.discussionTitle && (
                  <p className="text-xs text-muted-foreground truncate opacity-70">
                    {item.discussionTitle}
                  </p>
                )}
                {item.isRestricted && (
                  <p className="text-xs text-red-400 truncate">
                    Hidden by admin
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {data?.pagination && data.pagination.total > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing{' '}
            {Math.min((currentPage - 1) * 24 + 1, data.pagination.total)} to{' '}
            {Math.min(currentPage * 24, data.pagination.total)} of{' '}
            {data.pagination.total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= data.pagination.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Media Details Dialog */}
      <Dialog
        open={!!selectedMedia}
        onOpenChange={() => setSelectedMedia(null)}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Image className="h-5 w-5" />
              Media Details
            </DialogTitle>
            <DialogDescription>
              Uploaded by {selectedMedia?.uploaderUsername || 'Unknown'}
            </DialogDescription>
          </DialogHeader>
          {selectedMedia && (
            <div className="space-y-4 overflow-y-auto flex-1 pr-2">
              {/* Media Preview */}
              <div className="aspect-video bg-muted rounded-lg overflow-hidden flex items-center justify-center max-h-[300px]">
                <img
                  src={selectedMedia.url}
                  alt="Media preview"
                  loading="lazy"
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"%3E%3Crect x="3" y="3" width="18" height="18" rx="2" ry="2"%3E%3C/rect%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"%3E%3C/circle%3E%3Cpolyline points="21,15 16,10 5,21"%3E%3C/polyline%3E%3C/svg%3E';
                  }}
                />
              </div>

              {/* Media Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">
                    Category
                  </h4>
                  <p className="mt-1 capitalize">{selectedMedia.category}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">
                    Type
                  </h4>
                  <p className="mt-1 capitalize">
                    {selectedMedia.type?.replace('_', ' ')}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">
                    Source
                  </h4>
                  <p className="mt-1 capitalize">
                    {selectedMedia.sourceType || 'Unknown'}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">
                    Uploader
                  </h4>
                  <p className="mt-1 truncate">
                    {selectedMedia.uploaderUsername ||
                      selectedMedia.uploaderWallet?.slice(0, 10) ||
                      'Unknown'}
                  </p>
                </div>
              </div>

              {/* Wallet Address */}
              {selectedMedia.uploaderWallet && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">
                    Wallet Address
                  </h4>
                  <p className="mt-1 font-mono text-xs break-all">
                    {selectedMedia.uploaderWallet}
                  </p>
                </div>
              )}

              {selectedMedia.discussionTitle && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">
                    Discussion
                  </h4>
                  <p className="mt-1 truncate">
                    {selectedMedia.discussionTitle}
                  </p>
                </div>
              )}

              {/* URL */}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">
                  URL
                </h4>
                <p className="mt-1 font-mono text-xs break-all text-muted-foreground">
                  {selectedMedia.url}
                </p>
              </div>

              {/* Restricted Info */}
              {selectedMedia.isRestricted && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-red-400">
                    <EyeOff className="h-4 w-4" />
                    <span className="font-medium">
                      This media is hidden from public view
                    </span>
                  </div>
                  {selectedMedia.restrictedAt && (
                    <p className="text-xs text-red-400/70 mt-1">
                      Hidden{' '}
                      {formatDistanceToNow(
                        new Date(selectedMedia.restrictedAt),
                        { addSuffix: true }
                      )}
                      {selectedMedia.restrictedReason &&
                        ` - ${selectedMedia.restrictedReason}`}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-shrink-0 flex-col sm:flex-row gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => window.open(selectedMedia?.url, '_blank')}
              className="w-full sm:w-auto"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Original
            </Button>
            {selectedMedia?.isRestricted ? (
              <Button
                variant="secondary"
                onClick={() => {
                  handleAction('restore', selectedMedia);
                }}
                className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white"
              >
                <Eye className="h-4 w-4 mr-2" />
                Unhide Media
              </Button>
            ) : (
              <Button
                variant="secondary"
                onClick={() => {
                  handleAction('hide', selectedMedia);
                }}
                className="w-full sm:w-auto"
              >
                <EyeOff className="h-4 w-4 mr-2" />
                Hide Media
              </Button>
            )}
            <Button
              variant="destructive"
              onClick={() => {
                handleAction('delete', selectedMedia);
              }}
              className="w-full sm:w-auto"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Media
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <AlertDialog
        open={actionDialog.open}
        onOpenChange={(open) =>
          !open && setActionDialog({ open: false, type: null, media: null })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actionDialog.type === 'hide'
                ? 'Hide Media'
                : actionDialog.type === 'restore'
                ? 'Unhide Media'
                : 'Delete Media'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionDialog.type === 'hide'
                ? 'This will hide the media from public view. You can unhide it later.'
                : actionDialog.type === 'restore'
                ? 'This will restore the media and make it visible to the public again.'
                : 'This action cannot be undone. The media will be permanently deleted from the platform.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAction}
              className={
                actionDialog.type === 'delete'
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : actionDialog.type === 'restore'
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : ''
              }
            >
              {actionDialog.type === 'hide'
                ? 'Hide'
                : actionDialog.type === 'restore'
                ? 'Unhide'
                : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Deleted Discussions Tab (User soft-deleted content)
function DeletedDiscussionsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [actionDialog, setActionDialog] = useState({
    open: false,
    discussion: null,
  });
  const [currentTime, setCurrentTime] = useState(Date.now());

  const currentPage = parseInt(searchParams.get('deletedPage') || '1');

  const { data, isLoading, refetch } = useAdminDeletedDiscussions({
    page: currentPage,
    limit: 20,
    search: searchQuery || undefined,
  });

  const restoreDiscussion = useAdminRestoreDeletedDiscussion();

  // Real-time countdown update every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handlePageChange = (newPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('deletedPage', newPage.toString());
    setSearchParams(newParams);
  };

  const handleRestore = (discussion) => {
    setActionDialog({ open: true, discussion });
  };

  const confirmRestore = async () => {
    if (!actionDialog.discussion) return;
    try {
      await restoreDiscussion.mutateAsync({
        discussionOid: actionDialog.discussion.oid,
        reason: 'Restored by admin',
      });
      refetch();
    } catch (error) {
      console.error('Restore failed:', error);
    }
    setActionDialog({ open: false, discussion: null });
  };

  // Calculate real-time remaining time based on deletedAt
  const getRealTimeRemaining = (deletedAt) => {
    if (!deletedAt) return 0;
    const deletedTime = new Date(deletedAt).getTime();
    const fiveMinutesInMs = 5 * 60 * 1000;
    const deadline = deletedTime + fiveMinutesInMs;
    return Math.max(0, deadline - currentTime);
  };

  const formatTimeRemaining = (ms) => {
    if (ms <= 0) return 'Expired';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-red-500" />
            Deleted Discussions
          </CardTitle>
          <CardDescription>
            Discussions that have been soft-deleted by users. Admins can restore
            these at any time.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="flex gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search deleted discussions..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !data?.discussions?.length ? (
            <div className="text-center py-8 text-muted-foreground">
              <Trash2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No deleted discussions found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Creator</TableHead>
                    <TableHead>Deleted At</TableHead>
                    <TableHead>Deleted By</TableHead>
                    <TableHead>User Restore Window</TableHead>
                    <TableHead>Has Original</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.discussions.map((discussion) => (
                    <TableRow key={discussion._id}>
                      <TableCell>
                        <div className="max-w-[200px]">
                          <p className="font-medium truncate">
                            {discussion.title}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {discussion.oid}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {discussion.creatorUsername ||
                            discussion.creator?.substring(0, 10) + '...'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {discussion.deletedAt
                            ? formatDistanceToNow(
                                new Date(discussion.deletedAt),
                                { addSuffix: true }
                              )
                            : 'Unknown'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {discussion.deletedBy?.substring(0, 10) || 'User'}...
                        </span>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const timeRemaining = getRealTimeRemaining(
                            discussion.deletedAt
                          );
                          const canRestore = timeRemaining > 0;
                          return canRestore ? (
                            <Badge
                              variant="outline"
                              className="text-emerald-500 border-emerald-500 font-mono"
                            >
                              <Clock className="h-3 w-3 mr-1" />
                              {formatTimeRemaining(timeRemaining)}
                            </Badge>
                          ) : (
                            <Badge
                              variant="secondary"
                              className="text-zinc-500"
                            >
                              Expired
                            </Badge>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {discussion.hasOriginalContent ? (
                          <Badge
                            variant="outline"
                            className="text-green-500 border-green-500"
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Yes
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-amber-500">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            No
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-emerald-500 border-emerald-500 hover:bg-emerald-500/10"
                          onClick={() => handleRestore(discussion)}
                        >
                          <RotateCcw className="h-4 w-4 mr-1" />
                          Restore
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {data.pagination && data.pagination.pages > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </Button>
                  <span className="flex items-center px-3 text-sm text-muted-foreground">
                    Page {currentPage} of {data.pagination.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === data.pagination.pages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Restore Confirmation Dialog */}
      <AlertDialog
        open={actionDialog.open}
        onOpenChange={(open) =>
          !open && setActionDialog({ open: false, discussion: null })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-emerald-500" />
              Restore Discussion
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Are you sure you want to restore this discussion?</p>
                {actionDialog.discussion && (
                  <div className="bg-muted p-3 rounded-md mt-2">
                    <p className="font-medium text-foreground">
                      {actionDialog.discussion.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      by{' '}
                      {actionDialog.discussion.creatorUsername ||
                        actionDialog.discussion.creator}
                    </p>
                  </div>
                )}
                {actionDialog.discussion?.hasOriginalContent ? (
                  <p className="text-emerald-500 text-sm">
                    ✓ Original content will be restored
                  </p>
                ) : (
                  <p className="text-amber-500 text-sm">
                    ⚠ Original content may not be available - content will show
                    as "[DELETED BY USER]"
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRestore}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
            >
              Restore Discussion
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Main Content Page
export default function AdminContentPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Content Moderation</h1>
        <p className="text-muted-foreground">
          Manage discussions, comments, and media content across the platform
        </p>
      </div>

      <Tabs defaultValue="discussions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="discussions" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Discussions
          </TabsTrigger>
          <TabsTrigger value="deleted" className="flex items-center gap-2">
            <Trash2 className="h-4 w-4" />
            Deleted
          </TabsTrigger>
          <TabsTrigger value="media" className="flex items-center gap-2">
            <Image className="h-4 w-4" />
            Media
          </TabsTrigger>
        </TabsList>

        <TabsContent value="discussions">
          <DiscussionsTab />
        </TabsContent>

        <TabsContent value="deleted">
          <DeletedDiscussionsTab />
        </TabsContent>

        <TabsContent value="media">
          <MediaTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
